import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { MainLayout } from './components/layout';
import { DeploymentWizard } from './components/wizard';
import { DeploymentsPage } from './components/deployments';
import { ProductsPage, ProductDetailPage } from './components/products';
import { SettingsPage } from './components/settings';
import { DashboardPage } from './components/dashboard';
import { LoginPage, AuthCallback } from './components/auth';

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

/**
 * Protected route wrapper that requires authentication
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const styles = useStyles();

  // Allow bypassing auth in development for testing
  const bypassAuth = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

  if (bypassAuth) {
    return <>{children}</>;
  }

  // Show loading while authentication is in progress
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Authenticating..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Main application component with routing
 */
export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/deploy" element={<DeploymentWizard />} />
                <Route path="/deploy/:deploymentId" element={<DeploymentWizard />} />
                <Route path="/deployments" element={<DeploymentsPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/deploy" replace />} />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
